import React, { useState, useEffect } from 'react';
import { getErrorMessage } from '../utils/errorHandler';

interface AnnualProcessProps {
    apiFetch: (input: RequestInfo | URL, init?: RequestInit) => Promise<Response>;
    currentTenantId: number | null;
}

interface AnnualProcessStatus {
    target_year: number;
    is_closed: boolean;
    can_close: boolean;
    warnings: string[];
    errors: string[];
}

interface ExecuteResult {
    success: boolean;
    message: string;
    year_closed: number;
    entries_created: number;
}

const AnnualProcess: React.FC<AnnualProcessProps> = ({ apiFetch, currentTenantId }) => {
    const [selectedYear, setSelectedYear] = useState<number>(new Date().getFullYear() - 1);
    const [status, setStatus] = useState<AnnualProcessStatus | null>(null);
    const [loading, setLoading] = useState(false);
    const [executing, setExecuting] = useState(false);
    const [result, setResult] = useState<ExecuteResult | null>(null);
    const [error, setError] = useState<string | null>(null);
    const [tab, setTab] = useState<'closing' | 'carry-forward'>('closing');

    const fetchStatus = async () => {
        setLoading(true);
        setError(null);
        setResult(null);
        try {
            const res = await apiFetch(`/api/annual-process/status?year=${selectedYear}`);
            if (!res.ok) {
                if (res.status === 404) {
                    // Handle year not found gracefully?
                    throw new Error("指定した年度の会計期間が存在しません。");
                }
                throw new Error(await getErrorMessage(res));
            }
            const data = await res.json();
            setStatus(data);
        } catch (err: any) {
            setError(err.message);
            setStatus(null);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (currentTenantId) {
            fetchStatus();
        }
    }, [selectedYear, currentTenantId]);

    const handleExecute = async () => {
        if (!window.confirm(`${selectedYear}年度の年次更新を実行しますか？\n（次年度への繰越仕訳が作成され、年度が締め切られます）`)) {
            return;
        }

        setExecuting(true);
        setError(null);
        try {
            const res = await apiFetch('/api/annual-process/execute', {
                method: 'POST',
                body: JSON.stringify({
                    target_year: selectedYear,
                    create_carry_forward: true
                })
            });

            if (!res.ok) {
                throw new Error(await getErrorMessage(res));
            }

            const data = await res.json();
            setResult(data);
            fetchStatus(); // Refresh status
        } catch (err: any) {
            setError(err.message);
        } finally {
            setExecuting(false);
        }
    };

    const years = Array.from({ length: 11 }, (_, i) => new Date().getFullYear() - 5 + i);

    return (
        <div style={{ padding: '20px', fontFamily: "'MS UI Gothic', sans-serif" }}>
            <h2 style={{ fontSize: '16px', borderBottom: '1px solid #ccc', paddingBottom: '10px' }}>
                年次更新・繰越処理
            </h2>

            <div style={{ margin: '20px 0', display: 'flex', alignItems: 'center', gap: '10px' }}>
                <label>対象年度:</label>
                <select
                    value={selectedYear}
                    onChange={(e) => setSelectedYear(Number(e.target.value))}
                    style={{ padding: '5px' }}
                >
                    {years.map(y => (
                        <option key={y} value={y}>{y}年度</option>
                    ))}
                </select>
                <button onClick={fetchStatus} disabled={loading} style={{ padding: '5px 10px' }}>
                    状態確認
                </button>
            </div>

            {error && (
                <div style={{ backgroundColor: '#fee', color: '#c33', padding: '10px', marginBottom: '20px' }}>
                    {error}
                </div>
            )}

            {status && (
                <div style={{ border: '1px solid #ddd', padding: '20px', backgroundColor: '#f9f9f9' }}>
                    <div style={{ marginBottom: '15px' }}>
                        <span style={{ fontWeight: 'bold', marginRight: '10px' }}>状態:</span>
                        <span style={{
                            padding: '5px 10px',
                            backgroundColor: status.is_closed ? '#ccc' : '#e3f2fd',
                            color: status.is_closed ? '#666' : '#0d47a1',
                            borderRadius: '4px',
                            fontWeight: 'bold'
                        }}>
                            {status.is_closed ? '締切済' : '未締切'}
                        </span>
                    </div>

                    {status.warnings.length > 0 && (
                        <div style={{ marginBottom: '15px', backgroundColor: '#fff3e0', padding: '10px' }}>
                            <strong>警告:</strong>
                            <ul style={{ margin: '5px 0' }}>
                                {status.warnings.map((w, i) => <li key={i}>{w}</li>)}
                            </ul>
                        </div>
                    )}

                    {result && (
                        <div style={{ marginBottom: '15px', backgroundColor: '#e8f5e9', padding: '10px', border: '1px solid #c8e6c9' }}>
                            <div style={{ fontWeight: 'bold', color: '#2e7d32' }}>✅ 処理が完了しました</div>
                            <div>作成された仕訳数: {result.entries_created}</div>
                        </div>
                    )}

                    <div style={{ marginTop: '20px' }}>
                        <button
                            onClick={handleExecute}
                            disabled={executing || status.is_closed || status.errors.length > 0}
                            style={{
                                padding: '10px 20px',
                                backgroundColor: (status.is_closed || status.errors.length > 0) ? '#ccc' : '#d32f2f',
                                color: 'white',
                                border: 'none',
                                borderRadius: '4px',
                                cursor: (status.is_closed || status.errors.length > 0) ? 'not-allowed' : 'pointer',
                                fontWeight: 'bold'
                            }}
                        >
                            {executing ? '処理中...' : '年次更新実行（繰越・締切）'}
                        </button>
                        {status.is_closed && (
                            <span style={{ marginLeft: '10px', color: '#666' }}>
                                ※この年度は既に締め切られています。
                            </span>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};

export default AnnualProcess;
